import Button from "../../../core/Button";
import {
  SavedSearch,
  SearchFilters,
} from "../../../../types/neighborhoodSearch";
import { PROPERTY_TYPE_OPTIONS } from "../../constants";
import Checkbox from "src/components/core/Checkbox";
import { forwardRef, useState } from "react";
import { SavedSearchCardHeader } from "./SavedSearchCardHeader";
import { isCombinedSavedSearch } from "../../utils/isCombinedSavedSearch";
import { isAddressSavedSearch } from "../../utils/isAddressSavedSearch";
import { isNullOrUndefined } from "src/utils/objects";
import {
  getLookupStatusFromCode,
  getLookupStatusLabel,
} from "../../utils/savedSearchStatusHelpers";
import { ChevronDown, ChevronRight, Edit, Trash2 } from "lucide-react";
import { ConfirmDialog } from "src/components/core/ConfirmDialog";
import { RenameSearchDialog } from "../RenameSearchDialog";
import { useDeleteSavedSearchMutation } from "../../hooks/useDeleteSavedSearchMutation";
import { toast } from "src/components/shadcn/useToast";

interface SavedSearchCardProps {
  search: SavedSearch;
  isExpanded?: boolean;
  onClick?: () => void;
  onLoadClick?: (search: SavedSearch) => void;
  isSelected?: boolean;
  onSelectedChange?: (isSelected: boolean) => void;
  onExpandToggleClick?: () => void;
  isFocused?: boolean;
}

const formatRange = (min?: number | null, max?: number | null) => {
  if (isNullOrUndefined(min) && isNullOrUndefined(max)) return "Any";
  if (isNullOrUndefined(min)) return `Up to ${max}`;
  if (isNullOrUndefined(max)) return `${min}+`;
  if (min === max) return min.toString();
  return `${min} - ${max}`;
};

export const SavedSearchCard = forwardRef<HTMLDivElement, SavedSearchCardProps>(
  (
    {
      search,
      isExpanded = false,
      onClick,
      onLoadClick,
      isSelected,
      onSelectedChange,
      onExpandToggleClick,
      isFocused = false,
    },
    ref
  ) => {
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
    const deleteMutation = useDeleteSavedSearchMutation();

    const {
      name,
      result_count: numProperties,
      create_timestamp: createdAt,
      search_parameters: searchParameters,
      lookup_status: lookupStatus,
    } = search;

    const filters: SearchFilters =
      searchParameters?.searchFilters || ({} as SearchFilters);

    const propertyTypes = filters.propertyType
      ? typeof filters.propertyType === "string"
        ? filters.propertyType.split(",")
        : [filters.propertyType]
      : [];

    const beds = formatRange(filters.bedrooms?.min, filters.bedrooms?.max);
    const baths = formatRange(filters.bathrooms?.min, filters.bathrooms?.max);
    const squareFeet = formatRange(
      filters.squareFootage?.min,
      filters.squareFootage?.max
    );
    const yearBuilt = formatRange(
      filters.yearBuilt?.min,
      filters.yearBuilt?.max
    );
    const acreage = formatRange(filters.acreage?.min, filters.acreage?.max);

    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      onExpandToggleClick?.();
    };

    const handleHeaderClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      // Toggle selection when header is clicked
      onSelectedChange?.(!isSelected);
      // Set map center
      onClick?.();
    };

    const handleSelectedChange = (checked: boolean) => {
      onSelectedChange?.(checked);
    };

    const handleRenameClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsRenameDialogOpen(true);
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
      try {
        await deleteMutation.mutateAsync({
          listId: search.id,
          searchName: search.name,
        });

        // Show success toast
        toast({
          title: "Search deleted",
          description: (
            <>
              Successfully deleted <strong>{search.name}</strong>
            </>
          ),
          variant: "success",
        });

      } catch (error) {
        console.error("Failed to delete search:", error);
        // Show error toast
        toast({
          title: "Delete failed",
          description: "Failed to delete the search. Please try again.",
          variant: "destructive",
        });
      }
    };

    const hasExpansionPanel = !isAddressSavedSearch(search);

    return (
      <>
        <div
          className={`saved-search-card w-full flex flex-row gap-2 hover:bg-primary/5 text-sm max-w-3xl p-[10px] cursor-pointer border rounded  ${
            isFocused ? "border-orange-500" : "border-transparent"
          }`}
          onClick={handleClick}
          ref={ref}
        >
        <div className="flex flex-row gap-2">
          {hasExpansionPanel ? (
            !isExpanded ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )
          ) : (
            <ChevronRight className="w-4 h-4 invisible" />
          )}

          <Checkbox
            checked={isSelected}
            onCheckedChange={handleSelectedChange}
          />
        </div>
        <div className="flex flex-col gap-4 flex-1">
          <div onClick={handleHeaderClick}>
            <SavedSearchCardHeader
              name={name}
              numProperties={numProperties}
              createdAt={createdAt}
              isExpanded={isExpanded}
              lookupStatusCode={lookupStatus}
              lookupStatus={
                lookupStatus ? getLookupStatusFromCode(lookupStatus) : undefined
              }
              lookupStatusLabel={
                lookupStatus ? getLookupStatusLabel(lookupStatus) : undefined
              }
            />
          </div>
          {isExpanded && (
            <div className="border-gray-300 border-t pt-4 gap-3 flex flex-col">
              {isCombinedSavedSearch(search) &&
              searchParameters?.combinedSearchSources ? (
                <div>
                  <h3 className="font-semibold text-sm text-gray-800 mb-3">
                    This list is a combination of:
                  </h3>
                  <div className="flex flex-col gap-2">
                    {searchParameters.combinedSearchSources.map(
                      (source, index) => (
                        <span
                          key={index}
                          className="flex flex-row justify-between text-sm font-medium text-gray-500"
                        >
                          {source.searchName}
                          {source.searchResultCount && (
                            <span className="text-sm font-medium text-gray-500">
                              {source.searchResultCount} Properties
                            </span>
                          )}
                        </span>
                      )
                    )}
                  </div>
                </div>
              ) : (
                !isAddressSavedSearch(search) && (
                  <div className="flex flex-col gap-4">
                    <div>
                      <h3 className="font-semibold text-gray-700 mb-3">
                        Property Types:
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {propertyTypes.map((type) => {
                          const option = PROPERTY_TYPE_OPTIONS.find(opt => opt.value === type);
                          if (!option) return null;
                          return (
                            <div key={type} className="mr-2 flex items-center gap-2 text-gray-500">
                              <span className="h-5 w-5">{option.icon}</span>
                              <span>{option.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-700 mb-3">
                        Additional Info:
                      </h3>
                      <div className="grid grid-cols-2 gap-x-12 gap-y-2 text-md">
                        <div>
                          <p className="flex">
                            <span className="whitespace-nowrap text-gray-500">
                              Beds:&nbsp;
                            </span>
                            <span className="whitespace-nowrap text-gray-700">
                              {beds}
                            </span>
                          </p>
                        </div>
                        <div>
                          <p className="flex">
                            <span className="whitespace-nowrap text-gray-500">
                              Year Built:&nbsp;
                            </span>
                            <span className="whitespace-nowrap text-gray-700">
                              {yearBuilt}
                            </span>
                          </p>
                        </div>
                        <div>
                          <p className="flex">
                            <span className="whitespace-nowrap text-gray-500">
                              Baths:&nbsp;
                            </span>
                            <span className="whitespace-nowrap text-gray-700">
                              {baths}
                            </span>
                          </p>
                        </div>
                        <div>
                          <p className="flex">
                            <span className="whitespace-nowrap text-gray-500">
                              Acreage:&nbsp;
                            </span>
                            <span className="whitespace-nowrap text-gray-700">
                              {acreage}
                            </span>
                          </p>
                        </div>
                        <div>
                          <p className="flex">
                            <span className="whitespace-nowrap text-gray-500">
                              Square feet:&nbsp;
                            </span>
                            <span className="whitespace-nowrap text-gray-700">
                              {squareFeet}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )}

              {onLoadClick && (
                <Button
                  className="w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    onLoadClick(search);
                  }}
                >
                  Load Properties
                </Button>
              )}

              {/* Actions section - only shown when expanded */}
              <div className="border-gray-300 border-t pt-3 mt-4">
                <div className="flex gap-4 justify-end">
                  <button
                    className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer"
                    onClick={handleRenameClick}
                  >
                    <Edit className="h-3 w-3" />
                    Rename
                  </button>
                  <button
                    className="flex items-center gap-1 text-sm text-red-600 hover:text-red-800 cursor-pointer"
                    onClick={handleDeleteClick}
                  >
                    <Trash2 className="h-3 w-3" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dialogs - rendered outside the clickable card area */}
      <RenameSearchDialog
        open={isRenameDialogOpen}
        onOpenChange={setIsRenameDialogOpen}
        search={search}
      />

      <ConfirmDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        title="Delete Search"
        description={
          <>
            Are you sure you want to delete <strong>{search.name}</strong>? This action cannot be undone.
          </>
        }
        confirmText={deleteMutation.isPending ? "Deleting..." : "Delete"}
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        variant="destructive"
        isLoading={deleteMutation.isPending}
      />
      </>
    );
  }
);
